import SimpleModal from "app/components/Modals/SimpleModal";
import { VisibilityDefaultType } from "../types";
import PermissionChart from "app/storybookComponents/Charts/PermissionChart";
import Button from "app/storybookComponents/Button";
import {
  WEEKLY_CHECK_IN_VISIBILITY_DEFAULT_REPORT_INFORMATION,
  WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS,
  WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS,
  WEEKLY_CHECK_IN_VISIBILITY_LABELS,
} from "../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Nav } from "react-bootstrap";
import {
  faBuilding,
  faEnvelopeOpenText,
} from "@fortawesome/pro-regular-svg-icons";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getWeeklyCheckInCompanySettings,
  selectExecutiveWeeklyCheckInCompanyCustomList,
  selectUpdateWeeklyCheckInCompanySettingsStatus,
  selectWeeklyCheckInCompanySettings,
  updateWeeklyCheckInCompanySettings,
} from "../slice";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useMemo, useState } from "react";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import WarningModal from "app/storybookComponents/Modals/WarningModal";
import SimplePopover from "app/components/Popovers/SimplePopover";
import { getPeopleOrPerson } from "utils/helperFunctions";

interface Props {
  type?: VisibilityDefaultType;
  setType?: (type: VisibilityDefaultType) => void;
  isOpen: boolean;
  onClose: () => void;
}

interface BulletPoint {
  title: string;
  description?: string;
}

const WeeklyCheckInVisibilityDefaultsModal = ({
  type = "development-focused",
  setType,
  isOpen,
  onClose,
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const executiveWeeklyCheckInCompanyCustomList = useAppSelector(
    selectExecutiveWeeklyCheckInCompanyCustomList
  );
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const updateWeeklyCheckInCompanySettingsStatus = useAppSelector(
    selectUpdateWeeklyCheckInCompanySettingsStatus
  );
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const reportingLineManagerPopover = useMemo(() => {
    return (
      <SimplePopover
        popoverContent={
          <p style={{ marginTop: "-8px" }}>
            This includes all managers above this user’s manager in your
            organization’s reporting structure.
          </p>
        }
        popoverTitle="User’s Reporting Line Managers"
      >
        <FontAwesomeIcon icon={["far", "circle-info"]} className="ms-2" />
      </SimplePopover>
    );
  }, []);

  const renderVisibilityTableSection = () => {
    const template = WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS[type];

    return (
      <div>
        <PermissionChart
          template={template}
          columns={[
            { label: "Admin", key: "admin" },
            { label: "User's Manager", key: "usersManager" },
            { label: "User's Team Leaders", key: "teamLeaders" },
            {
              label: "User's Reporting Line Managers",
              key: "reportingLineManagers",
              additionalHeaderInfo: reportingLineManagerPopover,
            },
            { label: "Everyone Else", key: "everyoneElse" },
          ]}
          reportTitle="Manager Check-in"
          className="w-100"
        />
        <div className="warning-banner blue">
          <p>
            <FontAwesomeIcon icon={["far", "info-circle"]} className="me-2" />A
            user can always view their own response summary and check-in scores.
          </p>
        </div>
      </div>
    );
  };

  const getEmailReportCard = ({
    title,
    isDisabled,
    bulletPoints,
  }: {
    title: string;
    isDisabled?: boolean;
    bulletPoints?: BulletPoint[];
  }) => {
    const bulletPointIcon: IconProp = isDisabled
      ? ["far", "circle-xmark"]
      : ["fas", "circle-check"];

    return (
      <Card
        style={{ padding: "16px" }}
        className={`check-in-email-report-card ${isDisabled ? "disabled" : ""}`}
      >
        <h4>{title}</h4>
        {bulletPoints && (
          <ul className="check-in-email-report-card__list">
            {bulletPoints.map((bulletPoint, index) => (
              <li
                key={bulletPoint.title}
                className="check-in-email-report-card__list-item"
              >
                <FontAwesomeIcon
                  icon={bulletPointIcon}
                  className="check-in-email-report-card__list-item-icon"
                />
                <div>
                  <p>{bulletPoint.title}</p>
                  <p className="small-grey-text">{bulletPoint.description}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    );
  };

  const renderSeniorManagerReportCard = () => {
    const bulletPoints = [];
    let isDisabled = false;

    if (type !== WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.DEVELOPMENT) {
      bulletPoints.push({
        title: "No one will receive this report",
        description:
          "This email report is not available due to limited visibility by senior managers in this privacy setting.",
      });
      isDisabled = true;
    } else if (
      !weeklyCheckInCompanySettings?.hasWeeklyCheckInSeniorManagerReportEnabled
    ) {
      bulletPoints.push({
        title: "No one will receive this report",
        description:
          "You’ve disabled this email report type for your organization. You can enable this report in your organization settings.",
      });
      isDisabled = true;
    } else {
      bulletPoints.push(
        ...[
          {
            title: "Admins",
          },
          {
            title: "People added to custom list",
          },
          {
            title: "User’s reporting line managers",
            description: "This may include the user’s manager or team leaders.",
          },
        ]
      );
    }

    return getEmailReportCard({
      title: "Senior Manager Check-in Report Card",
      isDisabled,
      bulletPoints,
    });
  };

  const renderTeamLeaderReportCard = () => {
    const bulletPoints = [];
    let isDisabled = false;

    if (type === WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY) {
      bulletPoints.push({
        title: "No one will receive this report",
        description:
          "This email report is not available due to limited visibility by team leaders in this privacy setting.",
      });
      isDisabled = true;
    } else if (
      !weeklyCheckInCompanySettings?.hasWeeklyCheckInTeamLeaderReportEnabled
    ) {
      bulletPoints.push({
        title: "No one will receive this report",
        description:
          "You’ve disabled this email report type for your organization. You can enable this report in your organization settings.",
      });
      isDisabled = true;
    } else {
      bulletPoints.push(
        ...[
          {
            title: "Admins",
          },
          {
            title: "User’s team leader(s)",
            description: "This may include the user’s manager.",
          },
        ]
      );
    }

    return getEmailReportCard({
      title: "Team Leader Check-in Report Card",
      isDisabled,
      bulletPoints,
    });
  };

  const areAllAdminsIncludedInExecutiveCustomList = useMemo(() => {
    const executiveWeeklyCheckInCompanyCustomListObject =
      executiveWeeklyCheckInCompanyCustomList?.reduce(
        (acc, userAccountId) => ({
          ...acc,
          [userAccountId]: true,
        }),
        {} as Record<number, boolean>
      );
    return Object.values(usersById).every(
      (user) =>
        user.tmgRoleId !== 1 ||
        executiveWeeklyCheckInCompanyCustomListObject?.[user.userAccountId]
    );
  }, [executiveWeeklyCheckInCompanyCustomList, usersById]);

  const renderExecutiveReportCard = () => {
    const bulletPoints: BulletPoint[] = [];
    let isDisabled = true;

    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInExecutiveReportEnabledForAllAdmins ||
      areAllAdminsIncludedInExecutiveCustomList
    ) {
      bulletPoints.push({
        title: "Admins",
      });
      isDisabled = false;
    }

    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInExecutiveReportEnabledForCustomList
    ) {
      const totalPeopleAdded = executiveWeeklyCheckInCompanyCustomList.length;
      bulletPoints.push({
        title: "People added to custom list",
        description: `${totalPeopleAdded} ${getPeopleOrPerson(
          totalPeopleAdded
        )} added`,
      });
      isDisabled = false;
    }

    if (isDisabled) {
      bulletPoints.push({
        title: "No one will receive this report",
        description:
          "You’ve disabled this email report type for your organization. You can enable this report in your organization settings.",
      });
    }

    return getEmailReportCard({
      title: "Executive Check-in Report Card",
      isDisabled,
      bulletPoints,
    });
  };

  const onWarningModalConfirm = async () => {
    await dispatch(
      updateWeeklyCheckInCompanySettings({
        visibilityDefault: type,
      })
    );
    dispatch(getWeeklyCheckInCompanySettings({ forceRefresh: true }));
    setShowConfirmationModal(false);
    onClose();
  };

  const onCTAButtonClick = () => {
    setShowConfirmationModal(true);
  };

  const {
    bestForText,
    settingDescriptionText,
    benefitsList,
    emailReportSection,
    warningModalDescription,
  } = WEEKLY_CHECK_IN_VISIBILITY_DEFAULT_REPORT_INFORMATION[type];
  const weeklyCheckInVisibilityDefault =
    WEEKLY_CHECK_IN_VISIBILITY_LABELS[type];

  const showMainModal = isOpen && !showConfirmationModal;

  return (
    <>
      <WarningModal
        isOpen={showConfirmationModal}
        hideModal={() => setShowConfirmationModal(false)}
        warningTitle={`Are you sure you want to change visibility defaults for Manager Check-ins to ${weeklyCheckInVisibilityDefault}?`}
        warningMessage={warningModalDescription}
        onConfirmClick={onWarningModalConfirm}
        modalTitle="Confirmation"
        isDanger={false}
        includeHeaderLine
        cancelButtonText="Back"
        customButtonText="Confirm changes"
        isInProgress={updateWeeklyCheckInCompanySettingsStatus === "loading"}
      />
      <SimpleModal
        show={showMainModal}
        onHide={onClose}
        title="Manager Check-in Visibility Defaults"
        size="xl"
      >
        <div className="column-gap-16px">
          <div className="d-flex justify-content-between">
            <Nav
              className="simple-nav"
              activeKey={type}
              onSelect={(e) => {
                if (!e) return;
                setType?.(e as VisibilityDefaultType);
              }}
            >
              <Nav.Item>
                <Nav.Link eventKey="privacy-focused">Privacy Focused</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="team-focused">Team Focused</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="development-focused">
                  Development Focused{" "}
                  <span className="label-tag green nowrap">Recommended</span>
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <div>
              <Button onClick={onCTAButtonClick} style={{ width: "auto" }}>
                Change default to {weeklyCheckInVisibilityDefault}
              </Button>
            </div>
          </div>
          <div className="row-gap-16px align-items-center">
            <div className="small-square-icon">
              <FontAwesomeIcon icon={["far", "gear"]} className="icon" />
            </div>
            <h3>{settingDescriptionText}</h3>
          </div>
          <div className="row-gap-16px align-items-center">
            <div className="small-square-icon">
              <FontAwesomeIcon icon={faBuilding} className="icon" />
            </div>
            <div>
              <p>
                <b>Best For</b>
              </p>
              <p>{bestForText}</p>
            </div>
          </div>
          <div className="row-gap-16px">
            <div className="small-square-icon">
              <FontAwesomeIcon
                icon={["far", "lightbulb-on"]}
                className="icon"
              />
            </div>
            <div>
              <p>
                <b>Benefits</b>
              </p>
              <ul>
                {benefitsList.map((benefit) => (
                  <li key={benefit}>
                    <p>{benefit}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <hr className="m-0" />
          <h3>Visibility Details</h3>
          <div className="row-gap-16px">
            <div className="small-square-icon">
              <FontAwesomeIcon
                icon={
                  type === WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY
                    ? ["far", "eye"]
                    : ["fal", "clipboard-check"]
                }
                className="icon"
              />
            </div>
            <p>
              When a user completes a Manager Check-in, a response summary is
              generated and mood, progress, and readiness scores are assigned
              based on the response. The people who can view a user’s summary
              and check-in scores are shown below:
            </p>
          </div>
          {renderVisibilityTableSection()}
          <hr className="m-0" />
          <h3>Email Reports</h3>
          <div className="row-gap-16px">
            <div className="small-square-icon">
              <FontAwesomeIcon icon={faEnvelopeOpenText} className="icon" />
            </div>
            {emailReportSection}
          </div>
          <div
            className="multi-card-container-same-width"
            style={{
              gap: "12px",
            }}
          >
            {renderSeniorManagerReportCard()}
            {renderTeamLeaderReportCard()}
            {renderExecutiveReportCard()}
          </div>
          <div className="d-flex">
            <Button
              onClick={onCTAButtonClick}
              style={{ width: "auto", marginLeft: "auto" }}
            >
              Change default to {weeklyCheckInVisibilityDefault}
            </Button>
          </div>
        </div>
      </SimpleModal>
    </>
  );
};

export default WeeklyCheckInVisibilityDefaultsModal;
